import {AfterViewChecked, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Principal} from './core/auth/principal.service';
import {LoginService} from './core/login/login.service';
import {PublisherListFilter, PublishersService} from './shared/services/publishers.service';
import {AuthService} from './core/auth/auth.service';
import {Publisher} from './shared/domain/publisher.model';
import {SnackBarService} from './core/services/snackbar.service';
import {User} from './core/auth/user.model';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {PlaylistIngestFeedDialogComponent} from './modules/playlists/playlist-ingest-feed-dialog/playlist-ingest-feed-dialog.component';
import {PlaylistEditorDialogComponent} from './modules/playlists/playlist-editor-dialog.component';
import {ChannelEditorDialogComponent} from './modules/channels/channel-editor-dialog.component';
import {Router} from '@angular/router';
import {UserEditorDialogComponent} from './modules/users/user-editor/user-editor-dialog.component';
import { Observable, Subscription } from 'rxjs';
import {ContentOwnerService} from './shared/services/content-owner.service';
import {ContentOwner} from './shared/domain/content-owner.model';
import {AppRoutePath} from './app-route-path.enum';
import {environment} from '../environments/environment';
import { UserRole } from './core/auth/user-role.model';
import { RolePermission } from './core/auth/rbac/role-permission.service';
import { MatAccordion } from '@angular/material/expansion';
import { GlobalSidenavService } from './core/services/global-sidenav.service';
import { ChangePasswordDialogComponent } from './shared/components/change-password-dialog/change-password-dialog.component';
import {MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import { MatSidenav } from '@angular/material/sidenav';

@Component({
  selector: 'ctv-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('sidenav') sidenavRef: MatSidenav;
  @ViewChild('accordion') accordion: MatAccordion;
  @ViewChild('accordionNetwork') accordionNetwork: MatAccordion;

  public readonly AppRoutePath = AppRoutePath;
  public readonly RolePermission = RolePermission;
  public readonly pacingNavEnabled = environment.features?.pacingNav ?? false;
  isSideNavOpened$: Observable<boolean>;
  renderNav$: Observable<boolean>;
  screenWidth: number;
  mobileTopBar = document.getElementsByClassName('mobile-top-bar');
  scrollOffset: number;
  publishers: Publisher[];
  contentOwners: ContentOwner[];
  user: User;
  subscription = new Subscription();

  UI: {
    isNetworkUser?: boolean;
    isReadonlyAdmin?: boolean;
    isSalesRep?: boolean;
    isCampaignManager?: boolean;
    isRevOps?: boolean;
    isAdmin?: boolean;
    isDesigner?: boolean;
  } = {};

  constructor(public principal: Principal,
              private httpClient: HttpClient,
              private cdRef: ChangeDetectorRef,
              private loginService: LoginService,
              private authService: AuthService,
              private publisherService: PublishersService,
              private contentOwnerService: ContentOwnerService,
              private sbs: SnackBarService,
              private matDialog: MatDialog,
              private router: Router,
              public sideNavService: GlobalSidenavService) {
    this.screenWidth = window.innerWidth;
    this.sideNavService.setSideNavState(!this.screenWidth || this.screenWidth > 992);
    this.isSideNavOpened$ = this.sideNavService.isSideNavOpen$;
    this.renderNav$ = this.sideNavService.renderNav$;

    this.authService.updateUserHeaderData.subscribe((updateUserHeaderData: boolean) => {
      this.publishers = [];
      this.contentOwners = [];
    });
  }


  ngOnInit(): void {
    this.user = this.principal.user;
    this.UI.isNetworkUser = !!this.user.network;
    this.UI.isSalesRep = !!this.user.roles.includes(UserRole.SALES_REPRESENTATIVE);
    this.UI.isDesigner = !!this.user.roles.includes(UserRole.DESIGNER);
    this.UI.isRevOps = !!this.user.roles.includes(UserRole.REV_OPS);
    this.UI.isCampaignManager = !!this.user.roles.includes(UserRole.CAMPAIGN_MANAGER);
    this.UI.isAdmin = !!this.user.roles.includes(UserRole.ADMIN);
    this.UI.isReadonlyAdmin = !!this.user.roles.includes(UserRole.READ_ONLY_ADMIN);
    this.getUserPublishers();
    this.getUserCo();
  }

  getUserPublishers() {
    if (this.principal.user.publisherIds && this.principal.user.publisherIds.length) {
      this.subscription.add(
        this.publisherService.listPublishers({ids: this.principal.user.publisherIds} as PublisherListFilter).subscribe(publishers => {
          this.publishers = publishers.content;
        }, () => {
          this.sbs.error('Error during while fetching publishers');
        })
      );
    }
  }

  getUserCo() {
    if (this.principal.user.contentOwnerIds && this.principal.user.contentOwnerIds.length) {
      this.subscription.add(
        this.contentOwnerService.list({coIds: this.principal.user.contentOwnerIds}).subscribe(response => {
          this.contentOwners = response.filter(co => this.principal.user.contentOwnerIds.includes(co.id));
        }, () => {
          this.sbs.error('Error during while fetching content owners');
        })
      );
    }
  }

  public openChangePasswordDialog() {
    this.matDialog.open(ChangePasswordDialogComponent, <MatDialogConfig>{
      height: 'auto'
    });
  }

  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }


  public logout() {
    this.loginService.logout(true);
  }

  @HostListener('window:resize')
  resizeScreen() {
    this.screenWidth = window.innerWidth;
    this.sideNavService.setSideNavState(!this.screenWidth || this.screenWidth > 992);
  }

  @HostListener('window:scroll', ['$event']) onScrollEvent($event) {
    if ($event.path && $event.path.length) {
      this.scrollOffset = $event.path[1].scrollY;
    } else {
      this.scrollOffset = 0;
    }
  }

  openDashboard(type: 'co' | 'publisher', id?: string) {
    if (type === 'publisher') {
      window.open(`/#/p/${id}`, '_blank').focus();
    } else {
      window.open(`/#/co/${id}`, '_blank').focus();
    }
  }

  openDialog(type: string) {
    let modalComponent;
    switch (type) {
      case 'feed':
        modalComponent = PlaylistIngestFeedDialogComponent;
        break;
      case 'playlist':
        modalComponent = PlaylistEditorDialogComponent;
        break;
      case 'channel':
        modalComponent = ChannelEditorDialogComponent;
        break;
      case 'user':
        modalComponent = UserEditorDialogComponent;
        break;
      default:
        break;
    }
    // todo add publisher created

    if (modalComponent) {
      this.matDialog.open(modalComponent);
    }

    if (type === 'content-owner') {
      this.router.navigate(['content-owner/create']);
    }
  }

  closeSidenavMobile(sidenav: any) {
    if (this.screenWidth < 992) {
      //sidenav.toggle();
      this.sideNavService.toggle();
    }
  }

  closeAllExpansionsPanels(){
    this.accordion.multi = true;
    this.accordion.closeAll();
    this.accordion.multi = false;
  }

  closeAllNetworkExpansionsPanels(){
    this.accordionNetwork.multi = true;
    this.accordionNetwork.closeAll();
    this.accordionNetwork.multi = false;
  }

  getShortName(fullName) {
    return fullName.split(' ').map(n => n[0]).join('');
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}

